import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float, Stars, OrbitControls } from '@react-three/drei';
import FloatingShapes from './FloatingShapes';
import ParticleField from './ParticleField';
import CalculatorModel from './CalculatorModel';
import DocumentModel from './DocumentModel';
import AccountingShapes from './AccountingShapes';

const Scene = ({ mousePosition, isDarkMode = false }) => {
  const groupRef = useRef();

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.x = mousePosition.y * 0.2;
      groupRef.current.rotation.y = mousePosition.x * 0.2;
    }
  });

  return (
    <>
      {/* Background color */}
      <color attach="background" args={[isDarkMode ? '#000000' : '#FFFDD0']} />
      
      {/* Lighting */}
      <ambientLight intensity={isDarkMode ? 0.5 : 0.8} />
      <directionalLight position={[10, 10, 5]} intensity={isDarkMode ? 1 : 1.2} castShadow />
      <pointLight position={[-10, -10, -10]} color="#A37A00" intensity={isDarkMode ? 0.5 : 0.7} />
      <pointLight position={[10, 10, 10]} color="#B88A00" intensity={isDarkMode ? 0.3 : 0.4} />
      
      {/* Stars for both modes */}
      <Stars 
        radius={100} 
        depth={50} 
        count={5000} 
        factor={4} 
        saturation={0} 
        fade 
        color={isDarkMode ? '#ffffff' : '#A37A00'}
      />
      
      {/* Particle field */}
      <ParticleField count={200} isDarkMode={isDarkMode} />
      
      {/* Accounting shapes */}
      <AccountingShapes isDarkMode={isDarkMode} />
      
      {/* Main group that follows mouse */}
      <group ref={groupRef}>
        <FloatingShapes isDarkMode={isDarkMode} />
        
        {/* Calculator model */}
        <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
          <CalculatorModel position={[-4, 2, 0]} scale={0.8} isDarkMode={isDarkMode} />
        </Float>
        
        {/* Document model */}
        <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.3}>
          <DocumentModel position={[4, -1, 0]} scale={0.6} isDarkMode={isDarkMode} />
        </Float>
        
        {/* Floating gold spheres */}
        <mesh position={[0, 3, -2]}>
          <sphereGeometry args={[0.3, 32, 32]} />
          <meshStandardMaterial 
            color="#A37A00" 
            emissive="#A37A00" 
            emissiveIntensity={isDarkMode ? 0.3 : 0.2}
          />
        </mesh>
        
        {/* Floating torus */}
        <mesh position={[3, -2, -1]}>
          <torusGeometry args={[0.4, 0.1, 16, 100]} />
          <meshStandardMaterial 
            color="#8A6800" 
            emissive="#8A6800" 
            emissiveIntensity={isDarkMode ? 0.3 : 0.2}
          />
        </mesh>
        
        {/* Coin shape (cylinder) */}
        <Float speed={1.2} rotationIntensity={0.4} floatIntensity={0.4}>
          <mesh position={[-2, 4, -5]}>
            <cylinderGeometry args={[0.3, 0.3, 0.05, 16]} />
            <meshStandardMaterial 
              color="#A37A00" 
              emissive="#A37A00" 
              emissiveIntensity={isDarkMode ? 0.3 : 0.2}
              metalness={0.7}
              roughness={0.2}
            />
          </mesh>
        </Float>
        
        {/* Document/book shape */}
        <Float speed={0.8} rotationIntensity={0.2} floatIntensity={0.5}>
          <mesh position={[3, -3, -4]}>
            <boxGeometry args={[0.8, 1.1, 0.05]} />
            <meshStandardMaterial 
              color={isDarkMode ? "#2d3748" : "#4a5568"} 
              emissive={isDarkMode ? "#2d3748" : undefined} 
              emissiveIntensity={isDarkMode ? 0.05 : 0}
              metalness={0.3}
              roughness={0.6}
            />
          </mesh>
        </Float>
        
        {/* Ring shape */}
        <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.3}>
          <mesh position={[0, -1, -6]}>
            <torusGeometry args={[0.4, 0.08, 16, 32]} />
            <meshStandardMaterial 
              color="#8A6800" 
              emissive="#8A6800" 
              emissiveIntensity={isDarkMode ? 0.25 : 0.15}
              metalness={0.6}
              roughness={0.3}
            />
          </mesh>
        </Float>
      </group>
      
      {/* Camera controls */}
      <OrbitControls
        enableZoom={false}
        enablePan={false}
        maxPolarAngle={Math.PI / 2}
        minPolarAngle={Math.PI / 2}
        autoRotate
        autoRotateSpeed={0.5}
      />
    </>
  );
};

export default Scene;